import Bb from 'backbone';
import validate from 'validate.js';

import BaseFormView from '../../../components/BaseForm';
import Countries from '../../../../collections/Countries';
import UserModel from '../../../../models/User';
import { Preloader, redirectTo } from '../../../../utils';

validate.Promise = Promise;

class NoMatchStartPlayingView extends BaseFormView {

	get template() {
		// Overwrite in extended view
		return 'Overwrite template in extended view';
	}

	get className() {
		return "onboarding-form onboarding-no-match-start-playing";
	}

	get team_name_key() {
		// Overwrite in extended view
		return '';
	}

	get sponsor_key() {
		// Overwrite in extended view
		return '';
	}

	ui() {
		return {
			form     : '.js-no-match-start-playing-form',
			next     : '.js-next',
			country  : '.js-country',
			state    : '.js-state',
			t_and_cs : '.js-t-and-cs',
			team_name: '.js-team-name',
			sponsor  : '.js-sponsor',
		};
	}

	events() {
		return {
			'click @ui.next'    : 'onClickNext',
			'change @ui.form'   : 'onChangeForm',
			'change @ui.country': 'onChangeCountry'
		};
	}

	async initialize() {
		this.onboarding = this.getOption('onboarding');
		this.model = new Bb.Model();
		this.countries = new Countries();
		this.states = {
			AU: [
				{ id: 'ACT', name: 'Australian Capital Territory' },
				{ id: 'NT', name: 'Northern Territory' },
				{ id: 'NSW', name: 'New South Wales' },
				{ id: 'QLD', name: 'Queensland' },
				{ id: 'SA', name: 'South Australia' },
				{ id: 'TAS', name: 'Tasmania' },
				{ id: 'VIC', name: 'Victoria' },
				{ id: 'WA', name: 'Western Australia' }
			]
		};
		this.setDefaultValues();
		this.addCheckTeamNameValidator();

		Preloader.show();

		await this.getCountries();

		this.updateModel();

		Preloader.hide();

	}

	setDefaultValues() {
		let defaultValues = {
			countries: [],
			country  : this.onboarding.get('country') || 'AU',
			states   : this.states,
			state    : this.onboarding.get('state') || '',
			t_and_cs : 0,
			team_name: '',
			sponsor  : 0,
			errors   : undefined
		};

		this.model.set(defaultValues);
	}

	addCheckTeamNameValidator() {
		const team_name_key = this.team_name_key;
		const team_name_error_mapping = {
			'Team name must be unique': 'Team name already exists'
		};

		validate.validators.checkTeamName = function(value) {
			return new validate.Promise(function(resolve, reject) {
				UserModel.checkTeamName({ [team_name_key]: value }).then(response => {
					if (!response.success) {
						const error = response.errors[0].text;
						const parsedError = team_name_error_mapping[error] || error;
						return resolve(parsedError);
					}
					return resolve();
				}).catch(reject);
			});
		};
	}

	getCountries() {
		return this.countries.fetch();
	}

	updateModel() {
		this.model.set({
			countries: this.countries.toJSON()
		});

		this.render();
	}

	getFormValues() {
		return {
			country  : this.getUI( 'country' ).val(),
			state    : this.getUI( 'state' ).val(),
			t_and_cs : this.getUI( 't_and_cs' ).filter( ':checked' ).val() ? 1: 0,
			team_name: this.getUI( 'team_name' ).val(),
			sponsor  : this.getUI( 'sponsor' ).filter( ':checked' ).val() ? 1 : 0,
		};
	}

	validationRules() {
		return {
			country: {
				presence: {
					allowEmpty: false,
					message: "Country is required"
				}
			},
			state: (value, attributes) => {
				console.log('state validate:', value, attributes);
				if (this.model.get('country') === 'AU') {
					return {
						presence: {
							allowEmpty: false,
							message: "State is required"
						}
					}
				}

				return false;
			},
			t_and_cs: {
				exclusion: {
					within: [0],
					message: "Terms and Conditions must be checked"
				}
			},
			team_name:  {
				presence: {
					allowEmpty: false,
					message: "Team name is required"
				}
			}
		};
	}

	onChangeCountry() {
		this.model.set({
			country: this.getUI( 'country' ).val()
		});
		this.render();
	}

	/**
	 * Used to check if a field has a custom change event method that will be
	 * used instead of the standard changeField method. We only want one change
	 * event method to trigger. Any field that gets a custom change event method
	 * should be added to the `custom_change_method_fields` array within this method
	 *
	 * @param {string} name Name of field
	 * @returns {boolean}
	 * @memberof NoMatchStartPlayingView
	 */
	hasCustomChangeMethod(name) {
		const custom_change_method_fields = ['country'];

		return custom_change_method_fields.indexOf(name) !== -1;
	}

	async submit() {
		const errors = this.validate();

		if (errors) {
			this.attachValidationErrors(errors);
			return false;
		}

		Preloader.show();

		await this.validateOnServerPreSubmit().then(
			this.createUser.bind(this),
			this.attachValidationErrors.bind(this)
		);

		Preloader.hide();
	}

	async createUser() {
		Preloader.show();

		let newUser = {
			identity_token: UserModel.get('identity_token'),
			country       : this.model.get('country'),
			state         : this.model.get('state'),
			skill_level         : 'beginner',
			[this.team_name_key]: this.model.get('team_name'),
			[this.sponsor_key]  : this.model.get('sponsor'),
		};

		await UserModel.create(newUser);

		Preloader.hide();

		this.clearOnboardingAndRedirect();
	}

	clearOnboardingAndRedirect() {
		Preloader.show();
		this.onboarding.clearModelAndStore();

		this.completionRedirect();
	}

	completionRedirect() {
		// Overwrite this in extended view
		redirectTo();
	}

	serverValidationRulesPreSubmit() {
		let serverValidationRules = {
			team_name: {
				checkTeamName: true
			}
		};

		return serverValidationRules;
	}
}

export default NoMatchStartPlayingView;